/**
 * Canonical Desktop Tabulator runtime i18n (display-layer only).
 *
 * Contract:
 * - Update visible titles / filter option labels / placeholders IN PLACE
 * - Do NOT destroy/recreate the table
 * - Do NOT reload remote/progressive data for language-only changes
 * - Do NOT mutate raw filter values, mask field names, or API tokens
 * - Prefer setTabulatorColumnTitle (avoid Column.updateDefinition crash path)
 *
 * Progressive vs non-progressive: this helper is ownership-agnostic.
 */

import {
  applyTabulatorColumnTitles,
  setTabulatorColumnTitle,
} from './tabulatorColumnTitle';

export { applyTabulatorColumnTitles, setTabulatorColumnTitle };

function isDomElement(el) {
  return !!(el && typeof el.setAttribute === 'function' && typeof el.querySelector === 'function');
}

/**
 * Update header-filter display labels/placeholders without changing raw values.
 *
 * @param {object|null} table
 * @param {Record<string, {
 *   placeholder?: string,
 *   options?: Array<{ label: string, value: any }>,
 *   values?: Record<string, string>,
 * }>} [byField]
 */
export function applyTabulatorHeaderFilterDisplay(table, byField) {
  if (!table || typeof table.getColumns !== 'function' || !byField) return;
  const columns = table.getColumns();
  for (const col of columns) {
    const field = typeof col.getField === 'function' ? col.getField() : null;
    if (!field || !byField[field]) continue;
    const cfg = byField[field];
    const def = typeof col.getDefinition === 'function' ? col.getDefinition() : null;
    if (!def || typeof def !== 'object') continue;

    const prevParams = def.headerFilterParams && typeof def.headerFilterParams === 'object'
      ? def.headerFilterParams
      : {};
    const nextParams = { ...prevParams };

    if (cfg.placeholder != null) {
      nextParams.placeholder = cfg.placeholder;
    }
    if (Array.isArray(cfg.options)) {
      // Replace option labels; keep caller-supplied raw values.
      nextParams.options = cfg.options.map((opt) => ({
        label: opt.label,
        value: opt.value,
      }));
    }
    if (cfg.values && typeof cfg.values === 'object') {
      // values map: keys are raw tokens (immutable), values are display labels.
      nextParams.values = { ...cfg.values };
    }

    def.headerFilterParams = nextParams;

    try {
      if (typeof col.reloadHeaderFilter === 'function') {
        col.reloadHeaderFilter();
      }
    } catch (_) {
      /* keep live filters */
    }

    // Best-effort placeholder text on visible filter input.
    if (cfg.placeholder != null) {
      try {
        const el = typeof col.getElement === 'function' ? col.getElement() : null;
        if (isDomElement(el)) {
          const input = el.querySelector('.tabulator-header-filter input, .tabulator-header-filter textarea');
          if (input && input.getAttribute) {
            input.setAttribute('placeholder', String(cfg.placeholder));
          }
        }
      } catch (_) {
        /* ignore */
      }
    }
  }
}

/**
 * Reformat visible rows so locale-aware formatters that read a live translator
 * (ref / module translator) refresh without data reload.
 * Does not call replaceData / ajax.
 *
 * @param {object|null} table
 */
export function reformatTabulatorRowsForLocale(table) {
  if (!table || typeof table.getRows !== 'function') return;
  let rows;
  try {
    rows = table.getRows('visible') || table.getRows();
  } catch (_) {
    try { rows = table.getRows(); } catch (__) { return; }
  }
  if (!Array.isArray(rows)) return;
  for (const row of rows) {
    try {
      row.reformat?.();
    } catch (_) {
      /* keep session */
    }
  }
}

/**
 * Apply full display-layer locale update.
 *
 * @param {object|null} table
 * @param {(key: string, params?: object|null) => string} t
 * @param {{
 *   columnTitleKeys?: Record<string, string>,
 *   headerFilters?: Record<string, { placeholder?: string, options?: Array<{label:string,value:any}>, values?: Record<string,string> }>,
 *   onRefreshCells?: (table: object, t: Function) => void,
 *   reformatRows?: boolean,
 *   placeholder?: string | ((t: Function) => string),
 *   dataLoaderLoading?: string,
 *   dataLoaderError?: string,
 * }} [options]
 */
export function applyTabulatorRuntimeI18n(table, t, options = {}) {
  if (!table) return { applied: false };
  const {
    columnTitleKeys,
    headerFilters,
    onRefreshCells,
    reformatRows = true,
    placeholder,
    dataLoaderLoading,
    dataLoaderError,
  } = options;

  if (columnTitleKeys && typeof t === 'function') {
    applyTabulatorColumnTitles(table, t, columnTitleKeys);
  }

  if (headerFilters) {
    applyTabulatorHeaderFilterDisplay(table, headerFilters);
  }

  if (table.options && typeof table.options === 'object') {
    if (placeholder != null) {
      table.options.placeholder = typeof placeholder === 'function' ? placeholder(t) : placeholder;
    }
    if (dataLoaderLoading != null) table.options.dataLoaderLoading = dataLoaderLoading;
    if (dataLoaderError != null) table.options.dataLoaderError = dataLoaderError;
  }

  if (typeof onRefreshCells === 'function') {
    try {
      onRefreshCells(table, t);
    } catch (_) {
      /* keep session */
    }
  }

  if (reformatRows) {
    reformatTabulatorRowsForLocale(table);
  }

  return { applied: true };
}

/**
 * Decision: native Tabulator `langs` / `setLocale` is NOT wired globally.
 * App chrome (pagination off on most lists, empty state, titles) uses `t()`.
 * Native locale remains unused to avoid competing mechanisms.
 */
export const TABULATOR_NATIVE_LOCALE_POLICY = Object.freeze({
  used: false,
  reason: 'App-level t() owns titles/filters/empty; most lists disable Tabulator pagination. Avoid mixed locale sources.',
});

/**
 * Push fresh column definitions (titles, header filters, formatters) without remount.
 * Complements applyTabulatorRuntimeI18n for lists whose columns useMemo tracks locale.
 *
 * @param {object|null} table
 * @param {Array<object>|null} columns
 * @returns {boolean}
 */
export function applyTabulatorColumnsOnLocale(table, columns) {
  if (!table?.setColumns || !Array.isArray(columns)) return false;
  try {
    table.setColumns(columns);
  } catch (_) {
    return false;
  }
  try {
    table.redraw?.(true);
  } catch (_) {
    /* ignore redraw errors */
  }
  return true;
}
